import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Icon,
  IconButton,
  PageHeader,
  SaveBar,
  Skeleton,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  ALWAYS_VISIBLE,
  defaultLayout,
  HeroStyle,
  HIDEABLE_INFO,
  HIDEABLE_PARTS,
  HideablePart,
  moveItem,
  movableRows,
  withRows,
  MyMosqueLayout,
  normalizeLayout,
  sameLayout,
  SEASONAL,
  SECTION_INFO,
  SectionKey,
} from "./myMosqueLayout";
import styles from "./MyMosqueLayoutPage.module.scss";

const HERO_OPTIONS: Array<{
  value: HeroStyle;
  title: string;
  text: string;
}> = [
  {
    value: "FULL",
    title: "Stor",
    text: "Dagens topp med stor nedtelling til neste bønn.",
  },
  {
    value: "COMPACT",
    title: "Kompakt",
    text: "Halvparten så høy: neste bønn og nedtelling på to linjer.",
  },
];

/** Liten skisse av toppen i appen. */
const HeroSketch: React.FC<{ hero: HeroStyle }> = ({ hero }) => (
  <span className={styles.sketch} aria-hidden="true">
    {hero === "FULL" ? (
      <span className={styles.sketchFull}>
        <span className={styles.sketchBar} />
        <span className={styles.sketchRing} />
        <span className={styles.sketchLine} />
      </span>
    ) : (
      <span className={styles.sketchCompact}>
        <span className={styles.sketchRow}>
          <span className={styles.sketchDot} />
          <span className={styles.sketchBar} />
        </span>
        <span className={styles.sketchRow}>
          <span className={styles.sketchLine} />
          <span className={styles.sketchPill} />
        </span>
      </span>
    )}
    <span className={styles.sketchBlock} />
    <span className={styles.sketchBlock} />
  </span>
);

/** Seks prikker som dra-håndtak. */
const GripIcon: React.FC = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true">
    {[4, 8, 12].map((y) =>
      [5.5, 10.5].map((x) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={1.4} fill="currentColor" />
      )),
    )}
  </svg>
);

const MyMosqueLayoutPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const { execute } = useFetch<any>();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();
  const confirm = useConfirm();

  const [saved, setSaved] = useState<MyMosqueLayout>(defaultLayout());
  const [layout, setLayout] = useState<MyMosqueLayout>(defaultLayout());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragKey, setDragKey] = useState<SectionKey | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const listRef = useRef<HTMLOListElement>(null);

  const load = useCallback(async () => {
    if (!selectedOrgNumber) return;
    setIsLoading(true);
    setLoadError(false);
    const mosque = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}`,
      "GET",
    );
    if (mosque) {
      const current = normalizeLayout(mosque.myMosqueLayout);
      setSaved(current);
      setLayout(current);
    } else {
      setLoadError(true);
    }
    setIsLoading(false);
  }, [selectedOrgNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = !sameLayout(layout, saved);
  const isDefault = sameLayout(layout, defaultLayout());

  const rows = movableRows(layout);

  const announceMove = (key: SectionKey, sections: SectionKey[]) =>
    setAnnouncement(
      `${SECTION_INFO[key].name} er flyttet til plass ${
        sections.indexOf(key) + 1
      } av ${sections.length}.`,
    );

  const move = (key: SectionKey, delta: number) => {
    const from = rows.indexOf(key);
    const to = from + delta;
    if (to < 0 || to >= rows.length) return;
    const next = moveItem(rows, from, to);
    setLayout(withRows(layout, next));
    announceMove(key, next);
  };

  const setHidden = (part: HideablePart, hidden: boolean) =>
    setLayout(
      normalizeLayout({
        ...layout,
        hidden: hidden
          ? [...layout.hidden, part]
          : layout.hidden.filter((p) => p !== part),
      }),
    );

  // Dra med mus eller finger: draget starter på håndtaket og følges på hele vinduet, så det ikke
  // stopper når pekeren går over lagre-linjen nederst eller raden flyttes i DOM-en.
  const onPointerDown = (e: React.PointerEvent, key: SectionKey) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).focus();
    setDragKey(key);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragKey || !listRef.current) return;
    // Radene som ligger over pekeren (uten den som dras) gir plassen den skal inn på.
    const others = Array.from(
      listRef.current.querySelectorAll<HTMLElement>("[data-section]"),
    ).filter((row) => row.dataset.section !== dragKey);
    const target = others.filter((row) => {
      const rect = row.getBoundingClientRect();
      return rect.top + rect.height / 2 < e.clientY;
    }).length;
    const from = rows.indexOf(dragKey);
    if (from !== target) {
      setLayout(withRows(layout, moveItem(rows, from, target)));
    }
  };

  const onPointerUp = () => {
    if (!dragKey) return;
    announceMove(dragKey, rows);
    setDragKey(null);
  };

  const dragHandlers = useRef({ onPointerMove, onPointerUp });
  dragHandlers.current = { onPointerMove, onPointerUp };
  useEffect(() => {
    if (!dragKey) return;
    const move = (e: PointerEvent) => dragHandlers.current.onPointerMove(e);
    const up = () => dragHandlers.current.onPointerUp();
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [dragKey]);

  const onHandleKeyDown = (e: React.KeyboardEvent, key: SectionKey) => {
    const index = rows.indexOf(key);
    const last = rows.length - 1;
    const to =
      e.key === "ArrowUp"
        ? index - 1
        : e.key === "ArrowDown"
          ? index + 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;
    if (to === null) return;
    e.preventDefault();
    if (to < 0 || to > last || to === index) return;
    const next = moveItem(rows, index, to);
    setLayout(withRows(layout, next));
    announceMove(key, next);
  };

  const resetToDefault = async () => {
    const ok = await confirm({
      title: "Tilbakestille til standard?",
      message:
        "Stor topp, standard rekkefølge og alle seksjoner synlige. Endringen gjelder først når du lagrer.",
      confirmLabel: "Tilbakestill",
    });
    if (ok) setLayout(defaultLayout());
  };

  const save = async () => {
    if (!selectedOrgNumber) return;
    setSaving(true);
    const result = await executeRef.current(
      `/api/v1/mosques/${selectedOrgNumber}/my-mosque-layout`,
      "PUT",
      layout,
    );
    setSaving(false);
    if (result === null) {
      toast.error("Kunne ikke lagre oppsettet. Prøv igjen.");
      return;
    }
    const stored = result ? normalizeLayout(result) : layout;
    setSaved(stored);
    setLayout(stored);
    toast.success("Oppsettet er lagret. Appene viser det med en gang.");
  };

  const shownMosque = selectedOrgName || mosqueName || "moskeen";

  return (
    <>
      <PageHeader
        title="Min moské-oppsett"
        subtitle={`Bestem hvordan «Min moské» ser ut i appen for alle som følger ${shownMosque}.`}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={resetToDefault}
              disabled={isLoading || isDefault}
            >
              Tilbakestill til standard
            </Button>
            <Button
              onClick={save}
              loading={saving}
              disabled={isLoading || loadError || !dirty}
              icon={<Icon name="check" size={16} strokeWidth={2} />}
            >
              Lagre
            </Button>
          </>
        }
      />

      {loadError && (
        <Alert tone="error">
          Kunne ikke hente oppsettet for moskeen.{" "}
          <button type="button" className={styles.linkButton} onClick={load}>
            Prøv igjen
          </button>
        </Alert>
      )}

      <Card
        title="Toppen"
        subtitle="Den mørke toppen med neste bønn øverst på Min moské."
      >
        <div className={styles.heroOptions} role="radiogroup" aria-label="Topp">
          {HERO_OPTIONS.map((option) => {
            const on = layout.hero === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={isLoading}
                className={`${styles.heroOption} ${on ? styles.heroOn : ""}`}
                onClick={() => setLayout({ ...layout, hero: option.value })}
              >
                <HeroSketch hero={option.value} />
                <span className={styles.heroText}>
                  <b>
                    {option.title}
                    {on && (
                      <span className={styles.heroCheck}>
                        <Icon name="check" size={14} strokeWidth={3} />
                      </span>
                    )}
                  </b>
                  <span>{option.text}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className={styles.layout}>
        <Card
          title="Seksjoner"
          subtitle="Dra i håndtaket for å endre rekkefølgen, eller bruk pilene. Med tastatur: velg håndtaket og trykk pil opp/ned. En seksjon vises bare når den har innhold."
        >
          {isLoading ? (
            <div className={styles.skeletons}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={58} radius={14} />
              ))}
            </div>
          ) : (
            <ol ref={listRef} className={styles.list}>
              {rows.map((key, index) => {
                const info = SECTION_INFO[key];
                return (
                  <li
                    key={key}
                    data-section={key}
                    className={[
                      styles.row,
                      dragKey === key ? styles.dragging : "",
                      SEASONAL.includes(key) ? styles.rowSeasonal : "",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      className={styles.handle}
                      aria-label={`Flytt ${info.name}, plass ${index + 1} av ${
                        rows.length
                      }. Bruk pil opp og pil ned.`}
                      onPointerDown={(e) => onPointerDown(e, key)}
                      onKeyDown={(e) => onHandleKeyDown(e, key)}
                    >
                      <GripIcon />
                    </button>
                    <span className={styles.position}>{index + 1}</span>
                    <span className={styles.rowText}>
                      <b>{info.name}</b>
                      <span>{info.when}</span>
                    </span>
                    <span className={styles.rowActions}>
                      <IconButton
                        size="sm"
                        label="Flytt opp"
                        disabled={index === 0}
                        icon={
                          <Icon
                            name="chevronLeft"
                            size={16}
                            strokeWidth={2}
                            className={styles.up}
                          />
                        }
                        onClick={() => move(key, -1)}
                      />
                      <IconButton
                        size="sm"
                        label="Flytt ned"
                        disabled={index === rows.length - 1}
                        icon={
                          <Icon
                            name="chevronRight"
                            size={16}
                            strokeWidth={2}
                            className={styles.down}
                          />
                        }
                        onClick={() => move(key, 1)}
                      />
                      {key === ALWAYS_VISIBLE && (
                        <Badge tone="mint">Vises alltid</Badge>
                      )}
                      {SEASONAL.includes(key) && (
                        <Badge tone="gold">Bare i sesongen</Badge>
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
          <span className={ui.srOnly} aria-live="polite">
            {announcement}
          </span>
        </Card>

        <Card
          title="Kan skjules"
          subtitle="Delene nederst på Min moské som moskeen kan velge bort. Alt annet vises når det har innhold."
        >
          <ul className={styles.list}>
            {HIDEABLE_PARTS.map((part) => {
              const info = HIDEABLE_INFO[part];
              const hidden = layout.hidden.includes(part);
              return (
                <li
                  key={part}
                  className={[styles.row, hidden ? styles.rowHidden : ""].join(
                    " ",
                  )}
                >
                  <span className={styles.rowText}>
                    <b>{info.name}</b>
                    <span>{info.text}</span>
                  </span>
                  <span className={styles.rowActions}>
                    <Toggle
                      className={styles.toggle}
                      checked={!hidden}
                      disabled={isLoading}
                      aria-label={`Vis ${info.name}`}
                      label={
                        <span className={styles.toggleLabel}>
                          {hidden ? "Skjult" : "Vises"}
                        </span>
                      }
                      onChange={(e) => setHidden(part, !e.target.checked)}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card title="Forhåndsvisning" className={styles.previewCard}>
          <div className={styles.phone} aria-hidden="true">
            {layout.hero === "FULL" ? (
              <div className={styles.pFull}>
                <span className={styles.pName}>{shownMosque}</span>
                <span className={styles.pRing}>
                  <span>Isha</span>
                  <b>16 min</b>
                </span>
              </div>
            ) : (
              <div className={styles.pCompact}>
                <span className={styles.pName}>{shownMosque}</span>
                <span className={styles.pNext}>
                  Neste: Isha 18:31 · Jamat 18:30
                  <span className={styles.pPill}>om 16 min</span>
                </span>
              </div>
            )}
            <div className={styles.pSections}>
              {layout.sections.map((key) => (
                <span key={key} className={styles.pSection}>
                  {SECTION_INFO[key].name}
                </span>
              ))}
            </div>
          </div>
          <span className={styles.previewNote}>
            Skisse. Seksjoner uten innhold vises ikke i appen.
          </span>
        </Card>
      </div>

      <SaveBar
        visible={dirty && !isLoading}
        onSave={save}
        onDiscard={() => setLayout(saved)}
        saving={saving}
        saveLabel="Lagre"
      />
    </>
  );
};

export default MyMosqueLayoutPage;
